<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3>Создать новую группу товаров</h3>
      </div>

      <div v-if="loader" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <form class="modelReg__form" @submit.prevent="handleSubmit">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Название группы *</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите название группы"
            @blur="v$.name.$touch"
          />
          <div v-if="v$.name.$error" class="text-red-500 text-sm">
            {{ v$.name.$errors[0]?.$message || 'Это поле обязательно' }}
          </div>
        </div>

        <UiBaseInputN
          v-model="form.slug"
          type-input="text"
          label="Слаг"
          placeholder="Введите слаг (необязательно)"
        />

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Описание</label>
          <textarea
            v-model="form.description"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Введите описание группы"
          ></textarea>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Порядок сортировки</label>
          <input
            v-model.number="form.sort_order"
            type="number"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
        </div>

        <UiBaseInputN
          v-model="form.meta"
          type-input="text"
          label="Meta (JSON)"
          placeholder="Введите meta данные в формате JSON"
        />

        <!-- Выбор тегов -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Теги</label>
          <div v-if="loadingTags" class="text-sm text-gray-500">Загрузка тегов...</div>
          <div v-else-if="availableTags.length === 0" class="text-sm text-gray-500">
            Нет доступных тегов
          </div>
          <div v-else class="flex flex-wrap gap-2">
            <button
              v-for="tag in availableTags"
              :key="tag.id"
              type="button"
              :class="[
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                form.tags.includes(tag.id)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
              @click="toggleTag(tag.id)"
            >
              {{ tag.label }}
            </button>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex items-center">
            <input
              id="is_active"
              v-model="form.is_active"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="is_active" class="ml-2 block text-sm text-gray-900">
              Активна
            </label>
          </div>

          <div class="flex items-center">
            <input
              id="is_radio_button"
              v-model="form.is_radio_button"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="is_radio_button" class="ml-2 block text-sm text-gray-900">
              Показывать как радио кнопки
            </label>
          </div>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Изображение</label>
          <div class="relative">
            <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border border-gray-300">
              <img
                v-if="form.previewUrl"
                :src="form.previewUrl"
                class="w-full h-full object-cover"
                alt="Preview"
              />
              <span v-else class="text-gray-500 text-center text-sm">Загрузить изображение</span>
            </div>
            <input
              type="file"
              accept="image/*"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              @change="onImageChange"
            />
          </div>
        </div>

        <div class="modelReg__grid-btn">
          <UiButtonMain
            color="gray"
            variant="outline"
            label="Отмена"
            :disabled="loader"
            @click="closeModal"
          />
          <UiButtonMain
            color="sky"
            label="Создать группу"
            :disabled="loader || v$.$invalid"
            type="submit"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { consola } from 'consola';
import { apiAdminProduct } from '~/fsd-pages/admin/api';
import { APIAdminProductTags } from '~/entities/product/api';
import type { ProductTag } from '@entities/product';

const emit = defineEmits<{
  'close': []
  'group-created': []
  'update:size': [size: string]
}>();

const logger = consola.withTag('AddProductGroupModal');

const loader = ref(false);
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Список доступных тегов
const availableTags = ref<ProductTag[]>([]);
const loadingTags = ref(false);

// Форма
const form = reactive({
  name: '',
  slug: '',
  description: '',
  sort_order: 0,
  is_active: true,
  is_radio_button: false,
  meta: '',
  tags: [] as number[],
  previewUrl: undefined as string | undefined,
  _file: undefined as File | undefined,
});

// Валидация
const rules = {
  name: { required },
};

const v$ = useVuelidate(rules, form);

// Utility: если строка пустая/undefined/null, то null
const toNullIfEmpty = (v: any) =>
  typeof v === 'string' ? (v.trim() ? v.trim() : null) : v ?? null;

const loadTags = async () => {
  try {
    loadingTags.value = true;
    availableTags.value = await APIAdminProductTags.getAll(false); // Только активные теги
  } catch (error: any) {
    logger.error('Ошибка загрузки тегов:', error);
    errorMessage.value = 'Не удалось загрузить список тегов';
  } finally {
    loadingTags.value = false;
  }
};

const toggleTag = (tagId: number) => {
  const index = form.tags.indexOf(tagId);
  if (index === -1) {
    form.tags.push(tagId);
  } else {
    form.tags.splice(index, 1);
  }
};

const onImageChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  form.previewUrl = URL.createObjectURL(file);
  form._file = file;
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  if (!form.name || !form.name.trim()) {
    errorMessage.value = 'Название группы обязательно!';
    return;
  }

  loader.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const formData = new FormData();
    formData.append('name', form.name);

    // Не отправлять пустой slug
    const slugValue = toNullIfEmpty(form.slug);
    if (slugValue !== null) formData.append('slug', slugValue);

    formData.append('description', form.description ?? '');
    formData.append('sort_order', String(form.sort_order));
    formData.append('is_active', String(form.is_active));
    formData.append('is_radio_button', String(form.is_radio_button));
    formData.append(
      'meta',
      typeof form.meta === 'object'
        ? JSON.stringify(form.meta)
        : (form.meta ?? ''),
    );

    // Добавляем теги как JSON строку
    if (form.tags.length > 0) {
      formData.append('tags', JSON.stringify(form.tags));
    }

    if (form._file) {
      formData.append('image', form._file);
    }

    await apiAdminProduct.createProductGroup(formData);

    successMessage.value = 'Группа успешно создана!';

    // Сброс формы
    Object.assign(form, {
      name: '',
      slug: '',
      description: '',
      sort_order: 0,
      is_active: true,
      is_radio_button: false,
      meta: '',
      tags: [],
      previewUrl: undefined,
      _file: undefined,
    });

    v$.value.$reset();

    // Уведомляем родительский компонент
    emit('group-created');

    // Закрываем модал через 1 секунду
    setTimeout(() => {
      closeModal();
    }, 1000);

  } catch (error: any) {
    logger.error('Ошибка создания группы:', error);
    errorMessage.value = error?.response?.data?.message || error?.message || 'Ошибка сервера';
  } finally {
    loader.value = false;
  }
};

const closeModal = () => {
  emit('close');
};

onMounted(() => {
  emit('update:size', 'l');
  loadTags();
});
</script>

<style lang="scss" scoped>
.auth-modal {
  &__header {
    text-align: center;
    margin-bottom: 2rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  &__body {
    width: 100%;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    font-weight: 500;
    color: var(--text-primary);
    font-size: 0.875rem;
  }

  &__input {
    padding: 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    background: var(--bg-secondary);
    color: var(--text-primary);
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
    }

    &--error {
      border-color: var(--error-color);
    }
  }

  &__checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: var(--primary-color);
  }

  &__checkbox-label {
    font-size: 0.875rem;
    color: var(--text-primary);
    cursor: pointer;
  }

  &__image-upload {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  &__image-preview {
    width: 120px;
    height: 120px;
    border: 2px dashed var(--border-color);
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__image-placeholder {
    text-align: center;
    color: var(--text-secondary);
    font-size: 0.75rem;
    padding: 1rem;
  }

  &__file-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  &__error {
    color: var(--error-color);
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }

  &__error-message {
    background: var(--error-bg);
    color: var(--error-color);
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  &__success-message {
    background: var(--success-bg);
    color: var(--success-color);
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }

  &__button {
    flex: 1;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--primary {
      background: var(--primary-color);
      color: white;

      &:hover:not(:disabled) {
        background: var(--primary-hover);
      }
    }

    &--secondary {
      background: var(--bg-secondary);
      color: var(--text-primary);
      border: 1px solid var(--border-color);

      &:hover:not(:disabled) {
        background: var(--bg-hover);
      }
    }
  }
}
</style>